import { useEffect, useRef, useState } from "react";
import type { SkillDetailDTO, SkillFileDTO } from "../../shared/types";
import { post } from "../lib/api";
import { formatBytes } from "./Files";
import { fileToInput, type SkillFileInput } from "./SkillForm";
import { Badge, Button, ErrorText, inputCls } from "./ui";
import { Switch } from "./Switch";

const MAIN = "SKILL.md";
/** SKILL.md always first; the rest as the platform stores them. */
const order = (files: SkillFileDTO[]) => [...files].sort((a, b) => (a.path === MAIN ? -1 : b.path === MAIN ? 1 : a.path < b.path ? -1 : 1));

type Draft = { text: string; executable: boolean };

const byteSize = (file: SkillFileInput) =>
  file.text !== undefined
    ? new TextEncoder().encode(file.text).length
    : Math.floor((file.base64!.length * 3) / 4) - (file.base64!.endsWith("==") ? 2 : file.base64!.endsWith("=") ? 1 : 0);

/** What the platform infers for a file nobody marked: a script that says it is one. */
const executableOf = (file: SkillFileInput) => file.executable ?? (file.text?.startsWith("#!") ?? false);

/** A textarea hands back LF. A file that arrived with CRLF keeps them, so an edit changes only what was edited. */
const keepLineEndings = (stored: string, edited: string) => (stored.includes("\r\n") && !edited.includes("\r\n") ? edited.replace(/\n/g, "\r\n") : edited);

/**
 * The files of a skill: the paths on the left, the chosen one on the right. Saving appends a version, sending only
 * the files that changed and naming the rest in `keep`, so untouched bytes never travel twice — and a file the
 * editor cannot show is never rewritten from what the editor does not have.
 */
export function SkillFiles({ skill, dropped, onSaved }: { skill: SkillDetailDTO; dropped?: string[]; onSaved: (saved: SkillDetailDTO) => void }) {
  const [selected, setSelected] = useState(MAIN);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [added, setAdded] = useState<SkillFileInput[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  // A new version resets the editor: what is on screen is what the platform now stores.
  useEffect(() => {
    setDrafts({});
    setAdded([]);
    setRemoved([]);
    setSelected(MAIN);
    setError(null);
  }, [skill.currentVersionId]);

  // Reported back by whichever answer rewrote the header — the save just made, or the create that opened this popup.
  const ignored = skill.ignoredHeaderKeys ?? dropped ?? [];
  const stored = order(skill.files).filter((f) => !removed.includes(f.path));
  const shown: SkillFileDTO[] = [
    ...stored,
    ...added.map((f) => ({ path: f.path, size: byteSize(f), executable: executableOf(f), text: f.text ?? null })),
  ];
  const current = shown.find((f) => f.path === selected) ?? shown[0];
  const draftOf = (file: SkillFileDTO): Draft => drafts[file.path] ?? { text: file.text ?? "", executable: file.executable };
  const setDraft = (file: SkillFileDTO, patch: Partial<Draft>) => setDrafts({ ...drafts, [file.path]: { ...draftOf(file), ...patch } });
  const changed = Object.keys(drafts).length > 0 || added.length > 0 || removed.length > 0;

  /** A file added by mistake goes away; one the platform holds is marked for removal. Either way its draft goes too. */
  const remove = (path: string) => {
    if (added.some((f) => f.path === path)) setAdded(added.filter((f) => f.path !== path));
    else setRemoved([...removed, path]);
    setDrafts(Object.fromEntries(Object.entries(drafts).filter(([at]) => at !== path)));
    setSelected(MAIN);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const edited: SkillFileInput[] = [];
      const keep: string[] = [];
      for (const file of skill.files) {
        if (removed.includes(file.path)) continue;
        const draft = drafts[file.path];
        if (draft && (draft.text !== (file.text ?? "") || draft.executable !== file.executable)) {
          edited.push({ path: file.path, text: keepLineEndings(file.text ?? "", draft.text), executable: draft.executable });
        } else {
          keep.push(file.path);
        }
      }
      for (const file of added) {
        const draft = drafts[file.path];
        edited.push(draft ? { path: file.path, text: draft.text, executable: draft.executable } : file);
      }
      onSaved(await post<SkillDetailDTO>(`/api/skills/${skill.id}/versions`, { files: edited, keep, baseVersion: skill.version }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {ignored.length > 0 && (
        <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">
          The header is the platform's: {ignored.join(", ")} {ignored.length === 1 ? "was" : "were"} dropped, so the skill carries nothing dormant.
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[13rem_1fr]">
        <div className="space-y-1">
          {shown.map((file) => (
            <button
              key={file.path}
              type="button"
              onClick={() => setSelected(file.path)}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs ${file.path === current?.path ? "bg-fill font-medium" : "text-muted hover:text-fg"}`}
            >
              <span className="min-w-0 flex-1 truncate font-mono">{file.path}</span>
              {draftOf(file).executable && <Badge>exe</Badge>}
              {file.text === null && <span className="text-tiny text-faint">{formatBytes(file.size)}</span>}
            </button>
          ))}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => picker.current?.click()}>Add a file</Button>
            {current && current.path !== MAIN && (
              <Button variant="ghost" size="sm" onClick={() => remove(current.path)}>
                Remove {current.path}
              </Button>
            )}
          </div>
          <input
            ref={picker}
            type="file"
            multiple
            hidden
            onChange={async (e) => {
              const picked = Array.from(e.target.files ?? []);
              e.target.value = "";
              const next = await Promise.all(picked.map((file) => fileToInput(file, file.name)));
              setAdded([...added, ...next.filter((f) => !shown.some((already) => already.path === f.path))]);
              if (next[0]) setSelected(next[0].path);
            }}
          />
        </div>
        <div className="min-w-0 space-y-2">
          {!current ? (
            <p className="text-sm text-muted">No files.</p>
          ) : current.text === null ? (
            <p className="rounded-xl bg-fill-subtle px-3 py-6 text-center text-sm text-muted">
              binary · {formatBytes(current.size)} — kept as it is, and written into the run's folder unchanged.
            </p>
          ) : (
            <>
              <textarea className={`${inputCls} min-h-72 font-mono text-xs`} value={draftOf(current).text} onChange={(e) => setDraft(current, { text: e.target.value })} />
              {current.path !== MAIN && (
                <Switch
                  size="sm"
                  label="Executable"
                  hint="The run may run it directly."
                  checked={draftOf(current).executable}
                  onChange={(executable) => setDraft(current, { executable })}
                />
              )}
            </>
          )}
          <ErrorText error={error} />
          <div className="flex items-center justify-end gap-3">
            {removed.length > 0 && <span className="text-xs text-muted">Removing {removed.join(", ")}</span>}
            <Button variant="primary" disabled={busy || !changed} onClick={() => void save()}>
              {busy ? "Saving…" : "Save as new version"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
